import React, { useState, useEffect, useCallback } from 'react'
import { Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Button } from '@mui/material'
import { toast } from 'react-toastify'
import { deadLetterService } from '../../services/dead-letter-service'
import DeadLetterList from '../../components/dead-letter-list'

const STATUSES = ['pending', 'reprocessing', 'resolved']

const DeadLetter = () => {
  const [data, setData] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingId, setLoadingId] = useState(null)
  const [topicFilter, setTopicFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('pending')
  const [availableTopics, setAvailableTopics] = useState([])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {}
      if (topicFilter) filters.topic = topicFilter
      if (statusFilter) filters.status = statusFilter
      const response = await deadLetterService.fetch(rowsPerPage, page * rowsPerPage, filters)
      setData(response.data || [])
      setCount(response.total || 0)
    } catch (error) {
      console.error('Fetch failed:', error)
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, topicFilter, statusFilter])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    deadLetterService.fetchTopics().then(setAvailableTopics).catch(() => {})
  }, [])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRetrigger = async (id) => {
    setLoadingId(id)
    try {
      await deadLetterService.retrigger(id)
      toast.success('Message retriggered — republished to its original topic.')
      await fetchData()
    } catch (e) {
      toast.error(`Retrigger failed: ${e?.response?.data?.message || e.message}`)
    } finally {
      setLoadingId(null)
    }
  }

  const handleResolve = async (id) => {
    setLoadingId(id)
    try {
      await deadLetterService.resolve(id)
      toast.success('Marked as resolved.')
      await fetchData()
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.message || e.message}`)
    } finally {
      setLoadingId(null)
    }
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(0)
            }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Topic</InputLabel>
          <Select
            value={topicFilter}
            label="Topic"
            onChange={(e) => {
              setTopicFilter(e.target.value)
              setPage(0)
            }}
          >
            <MenuItem value="">All topics</MenuItem>
            {availableTopics.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setTopicFilter('')
            setStatusFilter('pending')
            setPage(0)
          }}
        >
          Reset Filters
        </Button>
      </Stack>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            color: 'white'
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <DeadLetterList
          data={data}
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onRetrigger={handleRetrigger}
          onResolve={handleResolve}
          loadingId={loadingId}
        />
      )}
    </>
  )
}

export default DeadLetter
